/* eslint-disable react-hooks/exhaustive-deps */
import { LosseLink, losseContentParse, useLoaderData } from '@ubo/losse-sjedel'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { useEffect } from 'react'
import CrossHair from '~/components/elements/CrossHair'
import type { Maybe, Component_Header_MenuHeader_Submenu, Component_Header_MenuHeader } from '~/graphql/types'
import useIsMobile from '~/hooks/useIsMobile'
import { type PageBodyData } from '~/templates/page'

export default function HeaderSubmenuBig({
  menuItem,
  fields,
  setCurrentIndex,
  setMobileMenuOpen
}: {
  menuItem: Component_Header_MenuHeader
  fields: Maybe<Component_Header_MenuHeader_Submenu>
  setCurrentIndex: (index: number) => void
  setMobileMenuOpen: (open: boolean) => void
}) {
  const page = useLoaderData<PageBodyData>()
  const isMobile = useIsMobile()

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') setCurrentIndex(-1)
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown)

    return () => {
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [])

  return (
    <motion.div
      className="fixed z-[60] left-0 top-0 w-full h-full bg-white flex flex-col justify-between overflow-y-auto"
      initial={isMobile ? { x: '100%' } : { y: '-100%' }}
      animate={isMobile ? { x: 0 } : { y: 0 }}
      exit={isMobile ? { x: '100%' } : { y: '-100%' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="container pt-10">
        <div className="flex items-center justify-between">
          <h2 className="title--large border-text text-white max-lg:hidden">{menuItem.link?.title}</h2>
          <button
            type="button"
            onClick={() => {
              setCurrentIndex(-1)
            }}
            className="flex items-center gap-2 text-xl lg:hidden font-bold"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none" className="w-7 h-7 rotate-90">
              <path
                d="M13.2528 6.31542C13.187 6.24903 13.1086 6.19633 13.0223 6.16037C12.936 6.12441 12.8434 6.1059 12.7499 6.1059C12.6564 6.1059 12.5638 6.12441 12.4775 6.16037C12.3912 6.19633 12.3128 6.24903 12.247 6.31542L9.00283 9.55958C8.93698 9.62597 8.85864 9.67867 8.77232 9.71463C8.686 9.75059 8.59342 9.7691 8.49991 9.7691C8.4064 9.7691 8.31382 9.75059 8.2275 9.71463C8.14119 9.67867 8.06284 9.62597 7.99699 9.55958L4.75283 6.31542C4.68698 6.24903 4.60864 6.19633 4.52232 6.16037C4.436 6.12441 4.34342 6.1059 4.24991 6.1059C4.1564 6.1059 4.06382 6.12441 3.9775 6.16037C3.89118 6.19633 3.81284 6.24903 3.74699 6.31542C3.61507 6.44813 3.54102 6.62766 3.54102 6.81479C3.54102 7.00192 3.61507 7.18145 3.74699 7.31417L6.99824 10.5654C7.39668 10.9634 7.93678 11.1869 8.49991 11.1869C9.06304 11.1869 9.60314 10.9634 10.0016 10.5654L13.2528 7.31417C13.3848 7.18145 13.4588 7.00192 13.4588 6.81479C13.4588 6.62766 13.3848 6.44813 13.2528 6.31542Z"
                fill="currentColor"
              />
            </svg>
            Terug
          </button>
          <button
            type="button"
            onClick={() => {
              if (!isMobile) {
                setCurrentIndex(-1)
              } else {
                setMobileMenuOpen(false)
              }
            }}
            className="hover:text-site-conversion-hover text-site-base max-lg:pr-6"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 lg:w-11 lg:h-11"
            >
              <g id="Group">
                <path
                  id="Vector"
                  d="M9.41416 8.00022L15.7072 1.70782C16.0979 1.31718 16.0979 0.683833 15.7072 0.293224C15.3166 -0.0974159 14.6833 -0.0974159 14.2927 0.293224L8.00022 6.58628L1.70782 0.293224C1.31718 -0.0974159 0.683833 -0.0974159 0.293224 0.293224C-0.0973846 0.683865 -0.0974159 1.31721 0.293224 1.70782L6.58628 8.00022L0.293224 14.2927C-0.0974159 14.6833 -0.0974159 15.3166 0.293224 15.7072C0.683865 16.0979 1.31721 16.0979 1.70782 15.7072L8.00022 9.41416L14.2926 15.7072C14.6833 16.0979 15.3166 16.0979 15.7072 15.7072C16.0979 15.3166 16.0979 14.6833 15.7072 14.2927L9.41416 8.00022Z"
                  fill="currentColor"
                />
              </g>
            </svg>
          </button>
        </div>
        <div className="grid lg:grid-cols-2 max-xs:px-0 max-lg:px-10 lg:px-0 gap-y-6 sm:gap-y-10 lg:gap-y-20 2xl:gap-y-32 gap-x-16 2xl:gap-x-20 xl:pl-16 2xl:pl-32 xl:pr-10 2xl:pr-20 mb-16 xl:mb-20 mt-10 sm:mt-16 lg:mt-20 2xl:mt-20 2xl:mb-20">
          {fields?.mainItems?.map((edge, index) => {
            const _items = edge?.items
            const lastItem = _items?.[_items?.length - 1]

            return (
              <div key={index} className="relative animate-fade-up" style={{ animationDelay: `${index * 150}ms` }}>
                <LosseLink to={edge?.link?.url} target={edge?.link?.target} className="group">
                  <h3 className="max-xs:text-xl max-lg:text-2xl font-bold lg:title--medium flex items-center gap-4 group-hover:underline">
                    <CrossHair className="max-lg:hidden absolute -left-12 -top-12 xl:-left-16 xl:-top-16 w-16 h-16 xl:w-20 xl:h-20 group-hover:text-site-conversion-hover" />
                    {losseContentParse(edge?.link?.title)}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      className="w-3 h-3 lg:w-5 lg:h-5 relative lg:top-1"
                    >
                      <g clipPath="url(#clip0_514_358)">
                        <path
                          d="M4.5 16.8752C4.50003 16.7274 4.52919 16.581 4.58581 16.4445C4.64243 16.3079 4.72541 16.1839 4.83 16.0794L10.5832 10.3262C10.7574 10.1521 10.8956 9.94533 10.9898 9.71781C11.0841 9.4903 11.1326 9.24644 11.1326 9.00017C11.1326 8.7539 11.0841 8.51004 10.9898 8.28252C10.8956 8.05501 10.7574 7.84829 10.5832 7.67417L4.83675 1.92767C4.63182 1.71549 4.51843 1.43131 4.52099 1.13634C4.52355 0.841371 4.64187 0.559207 4.85045 0.350623C5.05903 0.142039 5.3412 0.0237236 5.63617 0.0211604C5.93114 0.0185971 6.21532 0.131991 6.42749 0.336919L12.174 6.08342C12.9466 6.85749 13.3806 7.90649 13.3806 9.00017C13.3806 10.0939 12.9466 11.1428 12.174 11.9169L6.41999 17.6709C6.26262 17.8281 6.06218 17.9351 5.84401 17.9784C5.62585 18.0217 5.39974 17.9994 5.19426 17.9142C4.98879 17.829 4.81317 17.6849 4.6896 17.4999C4.56602 17.315 4.50004 17.0976 4.5 16.8752Z"
                          fill="#1D1D1B"
                        />
                      </g>
                    </svg>
                  </h3>
                </LosseLink>
                <div className="mt-2 sm:mt-8 flex flex-wrap gap-2 sm:gap-3 pr-10">
                  {_items?.map((edge, index) => {
                    const _function = page.rest.globalCounters.functions.find(
                      (f) => losseContentParse(f.name) === losseContentParse(edge?.link?.title)
                    )

                    return (
                      <LosseLink
                        key={index}
                        to={`${edge?.link?.url}${_function ? `?Functies=${_function.name}` : ''}`}
                        target={edge?.link?.target}
                        className={clsx(
                          'label--outline--black !border-2 btn--pseudo--conversion',
                          index + 1 < _items?.length ? '' : 'sm:hidden'
                        )}
                      >
                        {edge?.link?.title}
                        {_function ? ` (${_function?.count})` : ''}
                      </LosseLink>
                    )
                  })}
                </div>
                <div className="mt-3 max-sm:hidden">
                  <LosseLink
                    to={lastItem?.link?.url}
                    target={lastItem?.link?.target}
                    className="label--outline--black !border-2 inline-flex items-center gap-3 justify-center btn--pseudo--conversion"
                  >
                    {lastItem?.link?.title}
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g id="fi-ss-angle-small-right">
                        <path
                          id="Vector"
                          d="M10.8111 19.207L9.40012 17.793L14.6891 12.5L9.40012 7.20697L10.8151 5.79297L16.1001 11.086C16.475 11.461 16.6857 11.9696 16.6857 12.5C16.6857 13.0303 16.475 13.5389 16.1001 13.914L10.8111 19.207Z"
                          fill="#7C00E9"
                        />
                      </g>
                    </svg>
                  </LosseLink>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {fields?.footerTitle && (
        <div className="bg-site-base p-6 sm:py-10 max-sm:hidden">
          <div className="container flex justify-between items-center gap-10">
            <h2 className="text-lg font-bold md:title--medium xl:title--semi-large text-white">{fields?.footerTitle}</h2>
            <div>
              {fields?.footerLink?.title && (
                <LosseLink to={fields?.footerLink?.url} target={fields?.footerLink?.target} className="btn--conversion">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clipPath="url(#clip0_1565_4539)">
                      <path
                        d="M12.9999 1.50013C12.9999 1.23492 13.1053 0.980562 13.2928 0.793025C13.4803 0.605489 13.7347 0.500132 13.9999 0.500132C16.6512 0.503044 19.193 1.55755 21.0678 3.43228C22.9425 5.30701 23.997 7.84886 23.9999 10.5001C23.9999 10.7654 23.8945 11.0197 23.707 11.2072C23.5195 11.3948 23.2651 11.5001 22.9999 11.5001C22.7347 11.5001 22.4803 11.3948 22.2928 11.2072C22.1053 11.0197 21.9999 10.7654 21.9999 10.5001C21.9975 8.37913 21.1539 6.34568 19.6541 4.84591C18.1544 3.34613 16.1209 2.50251 13.9999 2.50013C13.7347 2.50013 13.4803 2.39478 13.2928 2.20724C13.1053 2.0197 12.9999 1.76535 12.9999 1.50013ZM13.9999 6.50013C15.0608 6.50013 16.0782 6.92156 16.8283 7.67171C17.5785 8.42185 17.9999 9.43927 17.9999 10.5001C17.9999 10.7654 18.1053 11.0197 18.2928 11.2072C18.4803 11.3948 18.7347 11.5001 18.9999 11.5001C19.2651 11.5001 19.5195 11.3948 19.707 11.2072C19.8945 11.0197 19.9999 10.7654 19.9999 10.5001C19.9983 8.90932 19.3657 7.38412 18.2408 6.25925C17.1159 5.13437 15.5907 4.50172 13.9999 4.50013C13.7347 4.50013 13.4803 4.60549 13.2928 4.79303C13.1053 4.98056 12.9999 5.23492 12.9999 5.50013C12.9999 5.76535 13.1053 6.0197 13.2928 6.20724C13.4803 6.39478 13.7347 6.50013 13.9999 6.50013ZM23.0929 17.2391C23.6724 17.8202 23.9978 18.6074 23.9978 19.4281C23.9978 20.2488 23.6724 21.036 23.0929 21.6171L22.1829 22.6661C13.9929 30.5071 -5.93707 10.5821 1.78292 2.36613L2.93292 1.36613C3.51469 0.802809 4.29485 0.491181 5.10462 0.498667C5.91439 0.506153 6.68866 0.832151 7.25992 1.40613C7.29092 1.43713 9.14391 3.84413 9.14391 3.84413C9.69374 4.42176 9.99982 5.18906 9.99851 5.98654C9.99721 6.78401 9.68863 7.55031 9.13691 8.12613L7.97892 9.58213C8.61976 11.1392 9.56198 12.5544 10.7514 13.7462C11.9409 14.9381 13.3541 15.8831 14.9099 16.5271L16.3749 15.3621C16.9508 14.8108 17.717 14.5026 18.5142 14.5015C19.3114 14.5004 20.0785 14.8065 20.6559 15.3561C20.6559 15.3561 23.0619 17.2081 23.0929 17.2391ZM21.7169 18.6931C21.7169 18.6931 19.3239 16.8521 19.2929 16.8211C19.0869 16.6169 18.8085 16.5023 18.5184 16.5023C18.2283 16.5023 17.9499 16.6169 17.7439 16.8211C17.7169 16.8491 15.6999 18.4561 15.6999 18.4561C15.564 18.5643 15.4022 18.6352 15.2306 18.6619C15.0589 18.6885 14.8832 18.67 14.7209 18.6081C12.7054 17.8577 10.8746 16.6829 9.35275 15.1632C7.83085 13.6436 6.65332 11.8146 5.89992 9.80013C5.83312 9.63559 5.81134 9.45622 5.83682 9.28047C5.86231 9.10472 5.93413 8.93893 6.04492 8.80013C6.04492 8.80013 7.65192 6.78213 7.67892 6.75613C7.88318 6.55012 7.99779 6.27175 7.99779 5.98163C7.99779 5.69152 7.88318 5.41315 7.67892 5.20713C7.64792 5.17713 5.80692 2.78213 5.80692 2.78213C5.59782 2.59464 5.32492 2.49423 5.04417 2.50148C4.76341 2.50873 4.49606 2.6231 4.29692 2.82113L3.14692 3.82113C-2.49507 10.6051 14.7759 26.9181 20.7209 21.3001L21.6319 20.2501C21.8454 20.0524 21.9736 19.7794 21.9895 19.4888C22.0053 19.1983 21.9076 18.9129 21.7169 18.6931Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                  {fields?.footerLink?.title}
                </LosseLink>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}
